// [CONTROLS] real-time control rule parsing (Phase 1: threshold rules only),
// plus the inverse serializer used by the visual rule builder.
// SIMULATION has no id token in the real grammar, so `SIMULATION TIME > 6:00`
// is 4 tokens, not 5 (see NO_ID_OBJECTS below) — an easy-to-miss asymmetry
// with every other object type.

import { isValidSwmmDate } from "../../model/swmmDateTime.ts";

export type RuleObjectKeyword =
  "GAGE" | "NODE" | "LINK" | "CONDUIT" | "PUMP" | "ORIFICE" | "WEIR" | "OUTLET" | "SIMULATION";

export type RuleAttribute =
  | "INTENSITY"
  | "DEPTH"
  | "MAXDEPTH"
  | "HEAD"
  | "VOLUME"
  | "INFLOW"
  | "FLOW"
  | "FULLFLOW"
  | "FULLDEPTH"
  | "LENGTH"
  | "SLOPE"
  | "VELOCITY"
  | "STATUS"
  | "SETTING"
  | "TIMEOPEN"
  | "TIMECLOSED"
  | "TIME"
  | "DATE"
  | "CLOCKTIME"
  | "DAYOFYEAR"
  | "DAY"
  | "MONTH"
  /** A GAGE `nHR_PRECIP` compound token — `n` read via `atoi` (leading digits only), accepted whenever it's in `1..48`. */
  | "PAST_PRECIP";

export interface RuleVariable {
  object: RuleObjectKeyword;
  /** `""` for SIMULATION, which has no id token. */
  id: string;
  attribute: RuleAttribute;
  /** Set only when `attribute === "PAST_PRECIP"` — the `n` in `nHR_PRECIP`. */
  pastPrecipHours?: number;
}

type RuleRelop = "=" | "<>" | "<" | "<=" | ">" | ">=";

export interface RulePremise {
  /** How this premise combines with the ones already accumulated — the first premise's own value is never read (`IF` always starts a fresh `result=TRUE`). */
  logicOp: "AND" | "OR";
  /** A harmless unused placeholder when `lhsExprName` is set instead. */
  lhs: RuleVariable;
  /** Set when this premise's LHS is a named EXPRESSION rather than a plain object/attribute triple. Mutually exclusive with a meaningful `lhs`. */
  lhsExprName?: string;
  relop: RuleRelop;
  /** Exactly one of `rhsLiteral`/`rhsVar` is set. Values are always compared in raw project units, never converted at parse time — a caller must convert its own live internal value back to raw units before comparing. */
  rhsLiteral: number | null;
  rhsVar: RuleVariable | null;
}

/**
 * A `PID <p> <i> <d>` action's persistent per-action state, initialized to
 * `e1=e2=0` once at parse time and then mutated in place by
 * `controlRuleEval.ts`'s `getPidSetting` every time this action's rule
 * fires thereafter — this object itself (not a copy) carries that state
 * across routing steps.
 */
export interface PidState {
  kp: number;
  ki: number;
  kd: number;
  e1: number;
  e2: number;
}

export interface RuleAction {
  target: RuleVariable;
  /** Already resolved to a plain number — `ON`/`OFF` become `1`/`0` for a PUMP STATUS action, a SETTING action's literal is used as-is (0-1). Meaningless (always 0) when `curveName`/`tseriesName`/`pid` is set instead — the actual setting is only known once evaluated each step. */
  value: number;
  /** A CURVE-modulated SETTING action — this rule's last-evaluated premise value is looked up on this curve every step. Mutually exclusive with `tseriesName`/`pid`. */
  curveName?: string;
  /** A TIMESERIES-modulated SETTING action — looked up at the current simulation time every step, independent of any premise value. Mutually exclusive with `curveName`/`pid`. */
  tseriesName?: string;
  /** A `PID <p> <i> <d>`-modulated SETTING action — `controlRuleEval.ts`'s `getPidSetting` recomputes this action's setting every step, mutating this same object's `e1`/`e2` in place. Mutually exclusive with `curveName`/`tseriesName`. */
  pid?: PidState;
}

export interface ParsedControlRule {
  name: string;
  /** Default 0 when no `PRIORITY` clause is present. */
  priority: number;
  premises: RulePremise[];
  thenActions: RuleAction[];
  elseActions: RuleAction[];
}

/** Every real premise object keyword, in the order the manual lists them — exported for `ControlRuleBuilder.tsx`'s premise object dropdown. */
export const PREMISE_OBJECT_KEYWORDS: RuleObjectKeyword[] = [
  "GAGE",
  "NODE",
  "LINK",
  "CONDUIT",
  "PUMP",
  "ORIFICE",
  "WEIR",
  "OUTLET",
  "SIMULATION",
];
const OBJECT_WORDS = new Set<RuleObjectKeyword>(PREMISE_OBJECT_KEYWORDS);

/** Valid ACTION target keywords — a plain LINK/NODE/GAGE/SIMULATION target is never valid. Exported for `ControlRuleBuilder.tsx`'s action/else-action object dropdown. */
export const ACTION_OBJECT_KEYWORDS: RuleObjectKeyword[] = [
  "PUMP",
  "CONDUIT",
  "ORIFICE",
  "WEIR",
  "OUTLET",
];

/** Attributes an ACTION can assign, per target object keyword — narrower than (and not derived from) `VALID_ATTRIBUTES`, the premise-side vocabulary. Exported for `ControlRuleBuilder.tsx`'s action attribute dropdown. */
export const ACTION_ATTRIBUTES: Readonly<
  Record<"PUMP" | "CONDUIT" | "ORIFICE" | "WEIR" | "OUTLET", RuleAttribute[]>
> = {
  PUMP: ["STATUS", "SETTING"],
  CONDUIT: ["STATUS"],
  ORIFICE: ["SETTING"],
  WEIR: ["SETTING"],
  OUTLET: ["SETTING"],
};

/** Widest `nHR_PRECIP` window the real engine's rolling per-gage buffer tracks — kept in sync with `runoff.ts`'s `MAXPASTRAIN`. */
const MAX_PAST_RAIN_HOURS = 48;

/** Object keywords with no id token — just SIMULATION here. Exported for `ControlRuleBuilder.tsx`'s id-column visibility. */
export const NO_ID_OBJECTS = new Set<RuleObjectKeyword>(["SIMULATION"]);

const TIMEOPEN_CLOSED: RuleAttribute[] = ["TIMEOPEN", "TIMECLOSED"];

/** Attributes legal for a given object keyword. Exported (read-only, never mutated) for `ControlRuleBuilder.tsx`'s attribute dropdown, cascading from whichever object keyword the user picks for a PREMISE. */
export const VALID_ATTRIBUTES: Readonly<
  Partial<Record<RuleObjectKeyword, ReadonlySet<RuleAttribute>>>
> = {
  GAGE: new Set(["INTENSITY"]),
  NODE: new Set(["DEPTH", "MAXDEPTH", "HEAD", "VOLUME", "INFLOW"]),
  LINK: new Set([
    "STATUS",
    "DEPTH",
    "FULLFLOW",
    "FULLDEPTH",
    "FLOW",
    "LENGTH",
    "SLOPE",
    "VELOCITY",
    ...TIMEOPEN_CLOSED,
  ]),
  CONDUIT: new Set([
    "STATUS",
    "DEPTH",
    "FULLFLOW",
    "FULLDEPTH",
    "FLOW",
    "LENGTH",
    "SLOPE",
    "VELOCITY",
    ...TIMEOPEN_CLOSED,
  ]),
  PUMP: new Set(["FLOW", "SETTING", "STATUS", ...TIMEOPEN_CLOSED]),
  ORIFICE: new Set(["FLOW", "SETTING", ...TIMEOPEN_CLOSED]),
  WEIR: new Set(["FLOW", "SETTING", ...TIMEOPEN_CLOSED]),
  OUTLET: new Set(["FLOW", "SETTING", ...TIMEOPEN_CLOSED]),
  SIMULATION: new Set(["TIME", "DATE", "CLOCKTIME", "DAY", "MONTH", "DAYOFYEAR"]),
};

export type { RuleRelop };
/** Every relational operator, exported for `ControlRuleBuilder.tsx`'s operator dropdown, in the order the manual lists them. */
export const RELOPS_LIST: RuleRelop[] = ["=", "<>", "<", "<=", ">", ">="];
const RELOPS = new Set<RuleRelop>(RELOPS_LIST);

/** `H:MM` or `HH:MM:SS` literal (SIMULATION TIME/CLOCKTIME's value form) to elapsed/clock days. */
export function parseClockLiteral(token: string): number | null {
  const parts = token.split(":").map(Number);
  if (parts.some((p) => Number.isNaN(p))) return null;
  const [h, m = 0, s = 0] = parts;
  return (h * 3600 + m * 60 + s) / 86400;
}

/**
 * A `MM/DD/YYYY` literal to days since the plain JS/UTC epoch, deliberately
 * not the real engine's OLE-Automation basis — this port's timestamps are
 * already plain epoch milliseconds everywhere, so a DATE premise only ever
 * needs to compare against another value built the same way
 * (`ControlRuleContext.currentDateDays`).
 *
 * The 2-digit-year convention must agree with `currentDateDays`'s own
 * basis: this function used to promote `"98"` to 2098 while
 * `currentDateDays` took it literally as year 98 — two disagreeing
 * conventions for the same kind of literal, a real bug. Now both take a
 * 2-digit year literally, and an out-of-range date like `13/45/2024`
 * fails to parse instead of silently rolling over to some other calendar
 * date via `Date.UTC`.
 */
export function parseDateLiteral(token: string): number | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(token);
  if (!m) return null;
  const [, mm, dd, yyyy] = m;
  const year = Number(yyyy);
  const month = Number(mm);
  const day = Number(dd);
  if (!isValidSwmmDate(year, month, day)) return null;
  const ms = Date.UTC(year < 100 ? year + 1900 : year, month - 1, day);
  const date = new Date(ms);
  if (year < 100) date.setUTCFullYear(year);
  return date.getTime() / 86_400_000;
}

function isRuleObjectKeyword(token: string): token is RuleObjectKeyword {
  return OBJECT_WORDS.has(token as RuleObjectKeyword);
}

/**
 * Parses one `<object> [<id>] <attribute>` variable reference starting at
 * `tokens[start]`. Returns `null` (not a thrown error) for anything Phase 1
 * doesn't support — an unsupported clause is silently skipped by the
 * caller rather than causing a hard parse failure over one unsupported rule.
 */
function parseVariable(
  tokens: string[],
  start: number
): { variable: RuleVariable; next: number } | null {
  const objectToken = tokens[start]?.toUpperCase();
  if (!objectToken || !isRuleObjectKeyword(objectToken)) return null;
  let i = start + 1;
  let id = "";
  if (!NO_ID_OBJECTS.has(objectToken)) {
    if (i >= tokens.length) return null;
    id = tokens[i];
    i++;
  }
  const attribToken = tokens[i]?.toUpperCase();
  if (!attribToken) return null;

  // A GAGE attribute is either the literal word INTENSITY or a compound
  // `nHR_PRECIP` token — a different parse than every other object
  // keyword's fixed attribute vocabulary below.
  if (objectToken === "GAGE") {
    if (attribToken === "INTENSITY") {
      return { variable: { object: "GAGE", id, attribute: "INTENSITY" }, next: i + 1 };
    }
    const hours = parseInt(tokens[i], 10); // leading digits only, trailing text ignored
    if (Number.isNaN(hours) || hours < 1 || hours > MAX_PAST_RAIN_HOURS) return null;
    return {
      variable: { object: "GAGE", id, attribute: "PAST_PRECIP", pastPrecipHours: hours },
      next: i + 1,
    };
  }

  const validForObject = VALID_ATTRIBUTES[objectToken];
  if (!validForObject || !validForObject.has(attribToken as RuleAttribute)) return null;
  return {
    variable: { object: objectToken, id, attribute: attribToken as RuleAttribute },
    next: i + 1,
  };
}

/**
 * A premise's RHS value token: `TIME`/`CLOCKTIME`/`TIMEOPEN`/`TIMECLOSED`
 * all share the same clock-literal parse, `DATE` -> date literal, `STATUS`
 * -> a keyword only (see below), everything else -> plain number. `null`
 * for an unparseable literal (the caller then tries it as a variable
 * reference instead).
 *
 * A previous version of this function only special-cased TIME/CLOCKTIME,
 * silently treating a TIMEOPEN/TIMECLOSED premise's "H:MM" RHS (e.g.
 * `0:10`) as a plain number (`NaN`), which made the whole premise (and
 * then the whole rule) silently disappear with no warning. Confirmed real
 * on two official EPA test fixtures (`gate_control_2.inp`/
 * `gate_control_3.inp`), where every TIMEOPEN/TIMECLOSED rule used exactly
 * this RHS form and was dropped outright.
 */
function parseRhsLiteral(token: string, attribute: RuleAttribute): number | null {
  if (attribute === "STATUS") {
    // A STATUS premise's RHS literal is only ever a keyword (ON/OFF tried
    // first, then OPEN/CLOSED), for either object type, with no numeric
    // fallback — a previous version of this function fell through to a
    // generic `Number()` parse here too, so `IF PUMP P1 STATUS = 1` (never
    // valid syntax) parsed fine while the only correct form (`STATUS = ON`)
    // failed outright.
    const upper = token.toUpperCase();
    if (upper in STATUS_WORDS) return STATUS_WORDS[upper];
    if (upper in CONDUIT_STATUS_WORDS) return CONDUIT_STATUS_WORDS[upper];
    return null;
  }
  if (
    attribute === "TIME" ||
    attribute === "CLOCKTIME" ||
    attribute === "TIMEOPEN" ||
    attribute === "TIMECLOSED"
  )
    return parseClockLiteral(token);
  if (attribute === "DATE") return parseDateLiteral(token);
  const n = Number(token);
  return Number.isNaN(n) ? null : n;
}

/** Harmless placeholder for `RulePremise.lhs` when `lhsExprName` is set instead — never actually read. */
const EXPRESSION_LHS_PLACEHOLDER: RuleVariable = {
  object: "SIMULATION",
  id: "",
  attribute: "TIME",
};

/** Resolves one premise operand token — a named VARIABLE substitutes transparently to its aliased triple, checked before falling back to a plain object/attribute parse. Used for both LHS and RHS. */
function resolveOperand(
  tokens: string[],
  start: number,
  namedVariables: ReadonlyMap<string, RuleVariable>
): { variable: RuleVariable; next: number } | null {
  const token = tokens[start];
  const aliased = token !== undefined ? namedVariables.get(token) : undefined;
  if (aliased) return { variable: aliased, next: start + 1 };
  return parseVariable(tokens, start);
}

function parsePremiseLine(
  tokens: string[],
  logicOp: "AND" | "OR",
  namedVariables: ReadonlyMap<string, RuleVariable>,
  expressionNames: ReadonlySet<string>
): RulePremise | null {
  // tokens[0] is the leading keyword (IF/AND/OR) — the operand starts at 1.
  // A named EXPRESSION is checked first, LHS-only, before even the
  // named-variable check.
  const lhsToken = tokens[1];
  let lhs: RuleVariable;
  let lhsExprName: string | undefined;
  let next: number;
  if (lhsToken !== undefined && expressionNames.has(lhsToken)) {
    lhs = EXPRESSION_LHS_PLACEHOLDER;
    lhsExprName = lhsToken;
    next = 2;
  } else {
    const lhsResult = resolveOperand(tokens, 1, namedVariables);
    if (!lhsResult) return null;
    lhs = lhsResult.variable;
    next = lhsResult.next;
  }

  const relopToken = tokens[next];
  if (!relopToken || !RELOPS.has(relopToken as RuleRelop)) return null;
  const relop = relopToken as RuleRelop;
  const rhsStart = next + 1;
  if (rhsStart >= tokens.length) return null;

  // An EXPRESSION premise's RHS is always plain-number parsed, same as
  // `parseRhsLiteral` does for every attribute besides TIME/CLOCKTIME/DATE.
  const rhsLiteral =
    lhsExprName !== undefined
      ? (() => {
          const n = Number(tokens[rhsStart]);
          return Number.isNaN(n) ? null : n;
        })()
      : parseRhsLiteral(tokens[rhsStart], lhs.attribute);
  if (rhsLiteral !== null) return { logicOp, lhs, lhsExprName, relop, rhsLiteral, rhsVar: null };

  const rhsResult = resolveOperand(tokens, rhsStart, namedVariables);
  if (!rhsResult) return null;
  return { logicOp, lhs, lhsExprName, relop, rhsLiteral: null, rhsVar: rhsResult.variable };
}

/** Exported alongside `CONDUIT_STATUS_WORDS` for `ControlRuleBuilder.tsx`'s STATUS-action value dropdown — a raw 0/1 numeric literal is not a valid action value, unlike a STATUS premise's value. */
export const STATUS_WORDS: Readonly<Record<string, number>> = { OFF: 0, ON: 1 };
export const CONDUIT_STATUS_WORDS: Readonly<Record<string, number>> = { CLOSED: 0, OPEN: 1 };

/**
 * An action line — `<object> <id> <attribute> = <value>`. A PUMP
 * `STATUS = ON/OFF`, a CONDUIT `STATUS = OPEN/CLOSED`, or a
 * PUMP/ORIFICE/WEIR/OUTLET `SETTING = <numeric literal 0-1>` /
 * `SETTING = CURVE <name>` / `SETTING = TIMESERIES <name>` /
 * `SETTING = PID <p> <i> <d>` is supported. A generic `LINK` keyword
 * action target is never valid either way.
 */
function parseActionLine(tokens: string[]): RuleAction | null {
  const varResult = parseVariable(tokens, 1);
  if (!varResult) return null;
  const { variable: target, next } = varResult;
  if (tokens[next] !== "=") return null;
  const valueToken = tokens[next + 1];
  if (valueToken === undefined) return null;

  if (target.attribute === "STATUS") {
    const words =
      target.object === "PUMP"
        ? STATUS_WORDS
        : target.object === "CONDUIT"
          ? CONDUIT_STATUS_WORDS
          : null;
    if (!words) return null; // LINK/ORIFICE/WEIR/OUTLET STATUS actions aren't real either way
    const status = words[valueToken.toUpperCase()];
    return status === undefined ? null : { target, value: status };
  }
  if (target.attribute === "SETTING") {
    const keyword = valueToken.toUpperCase();
    if (keyword === "CURVE") {
      const curveName = tokens[next + 2];
      return curveName === undefined ? null : { target, value: 0, curveName };
    }
    if (keyword === "TIMESERIES") {
      const tseriesName = tokens[next + 2];
      return tseriesName === undefined ? null : { target, value: 0, tseriesName };
    }
    if (keyword === "PID") {
      // Exactly 3 trailing numeric tokens (gain/integral-time/derivative-
      // time); `e1`/`e2` start at 0.
      const kp = Number(tokens[next + 2]);
      const ki = Number(tokens[next + 3]);
      const kd = Number(tokens[next + 4]);
      if (Number.isNaN(kp) || Number.isNaN(ki) || Number.isNaN(kd)) return null;
      return { target, value: 0, pid: { kp, ki, kd, e1: 0, e2: 0 } };
    }
    const n = Number(valueToken);
    if (Number.isNaN(n)) return null;
    return { target, value: n };
  }
  return null;
}

const EMPTY_NAMED_VARIABLES: ReadonlyMap<string, RuleVariable> = new Map();
const EMPTY_EXPRESSION_NAMES: ReadonlySet<string> = new Set();

/**
 * Resolves every `VARIABLE <name> = <object> [id] <attribute>` declaration
 * into a name -> aliased-triple map, reusing the same parsing `parseVariable`
 * does for a premise/action. A declaration whose tokens don't parse is
 * silently dropped.
 */
export function parseControlVariables(
  defs: { name: string; tokens: string[] }[]
): Map<string, RuleVariable> {
  const result = new Map<string, RuleVariable>();
  for (const def of defs) {
    const parsed = parseVariable(def.tokens, 0);
    if (parsed) result.set(def.name, parsed.variable);
  }
  return result;
}

/**
 * Parses one `ControlRule.body` into a structured rule — `null` when
 * nothing in the body parses into a usable premise (a rule with zero
 * premises would evaluate as vacuously true every step, never what an
 * unparseable rule actually means). A rule that parses some lines but not
 * others keeps just the parseable ones.
 *
 * `namedVariables`/`expressionNames` both default to empty, a no-op for a
 * project with neither declaration.
 */
export function parseControlRule(
  rule: { name: string; body: string },
  namedVariables: ReadonlyMap<string, RuleVariable> = EMPTY_NAMED_VARIABLES,
  expressionNames: ReadonlySet<string> = EMPTY_EXPRESSION_NAMES
): ParsedControlRule | null {
  const lines = rule.body
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const premises: RulePremise[] = [];
  const thenActions: RuleAction[] = [];
  const elseActions: RuleAction[] = [];
  let priority = 0;
  let section: "IF" | "THEN" | "ELSE" | null = null;

  for (const line of lines) {
    const tokens = line.split(/\s+/);
    const keyword = tokens[0]?.toUpperCase();
    if (keyword === "IF") {
      section = "IF";
      const p = parsePremiseLine(tokens, "AND", namedVariables, expressionNames);
      if (p) premises.push(p);
    } else if (keyword === "AND" && section === "IF") {
      const p = parsePremiseLine(tokens, "AND", namedVariables, expressionNames);
      if (p) premises.push(p);
    } else if (keyword === "OR" && section === "IF") {
      const p = parsePremiseLine(tokens, "OR", namedVariables, expressionNames);
      if (p) premises.push(p);
    } else if (keyword === "THEN") {
      section = "THEN";
      const a = parseActionLine(tokens);
      if (a) thenActions.push(a);
    } else if (keyword === "AND" && (section === "THEN" || section === "ELSE")) {
      const a = parseActionLine(tokens);
      if (a) (section === "THEN" ? thenActions : elseActions).push(a);
    } else if (keyword === "ELSE") {
      section = "ELSE";
      const a = parseActionLine(tokens);
      if (a) elseActions.push(a);
    } else if (keyword === "PRIORITY") {
      const p = Number(tokens[1]);
      if (!Number.isNaN(p)) priority = p;
    }
  }

  if (premises.length === 0 || (thenActions.length === 0 && elseActions.length === 0)) return null;
  return { name: rule.name, priority, premises, thenActions, elseActions };
}

// Serialization — the exact inverse of the parsing above, kept in the same
// file so both directions share one token/word vocabulary and can never
// drift apart. `ControlRuleBuilder.tsx`'s structured editor round-trips
// premise/action objects back into `ControlRule.body` text through here,
// which `parseControlRule` above then re-parses on the next render.

/** A premise/action's `<object> [id] <attribute>` tokens. A GAGE `PAST_PRECIP` variable reconstructs the `nHR_PRECIP` compound token from `pastPrecipHours` instead of emitting the literal (unparseable) attribute name. */
function variableTokens(v: RuleVariable): string[] {
  const attributeToken =
    v.object === "GAGE" && v.attribute === "PAST_PRECIP"
      ? `${v.pastPrecipHours}HR_PRECIP`
      : v.attribute;
  return NO_ID_OBJECTS.has(v.object)
    ? [v.object, attributeToken]
    : [v.object, v.id, attributeToken];
}

/** `H:MM` (or `H:MM:SS` when there's a nonzero seconds component) — the inverse of `parseClockLiteral`. */
export function formatClockLiteral(days: number): string {
  const totalSeconds = Math.round(days * 86400);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, "0");
  return s === 0 ? `${h}:${mm}` : `${h}:${mm}:${String(s).padStart(2, "0")}`;
}

/** `MM/DD/YYYY` — the inverse of `parseDateLiteral`. */
export function formatDateLiteral(days: number): string {
  const d = new Date(days * 86_400_000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}

/** A plain numeric literal, trimmed to a sane precision (avoids floating-point noise like `0.30000000000000004` leaking into the generated `.inp` text) — every attribute besides TIME/CLOCKTIME/DATE uses this, matching `parseRhsLiteral`'s own default branch. */
function formatPlainNumber(value: number): string {
  return String(Math.round(value * 10_000) / 10_000);
}

function formatValueForAttribute(value: number, attribute: RuleAttribute): string {
  if (attribute === "TIME" || attribute === "CLOCKTIME") return formatClockLiteral(value);
  if (attribute === "DATE") return formatDateLiteral(value);
  // A STATUS premise accepts only a keyword, never a plain number — ON/OFF
  // round-trips for either object type, unlike a STATUS action's own
  // object-gated vocabulary below (PUMP -> ON/OFF, CONDUIT -> OPEN/CLOSED).
  if (attribute === "STATUS") return value === 0 ? "OFF" : "ON";
  return formatPlainNumber(value);
}

function premiseToLine(p: RulePremise, keyword: "IF" | "AND" | "OR"): string {
  const lhsTokens = p.lhsExprName !== undefined ? [p.lhsExprName] : variableTokens(p.lhs);
  // An expression premise's RHS always parses as a plain number, never
  // TIME/CLOCKTIME/DATE formatted.
  const rhsTokens =
    p.rhsVar !== null
      ? variableTokens(p.rhsVar)
      : [
          p.lhsExprName !== undefined
            ? formatPlainNumber(p.rhsLiteral ?? 0)
            : formatValueForAttribute(p.rhsLiteral ?? 0, p.lhs.attribute),
        ];
  return [keyword, ...lhsTokens, p.relop, ...rhsTokens].join(" ");
}

function actionToLine(a: RuleAction, keyword: "THEN" | "ELSE" | "AND"): string {
  const tokens = [keyword, ...variableTokens(a.target), "="];
  if (a.target.attribute === "STATUS") {
    const words =
      a.target.object === "PUMP"
        ? STATUS_WORDS
        : a.target.object === "CONDUIT"
          ? CONDUIT_STATUS_WORDS
          : null;
    const word = words ? Object.keys(words).find((k) => words[k] === a.value) : undefined;
    tokens.push(word ?? Object.keys(words ?? STATUS_WORDS)[0]);
  } else if (a.curveName !== undefined) {
    tokens.push("CURVE", a.curveName);
  } else if (a.tseriesName !== undefined) {
    tokens.push("TIMESERIES", a.tseriesName);
  } else if (a.pid !== undefined) {
    tokens.push(
      "PID",
      formatPlainNumber(a.pid.kp),
      formatPlainNumber(a.pid.ki),
      formatPlainNumber(a.pid.kd)
    );
  } else {
    tokens.push(formatPlainNumber(a.value));
  }
  return tokens.join(" ");
}

/**
 * Turns a structured rule back into `ControlRule.body`'s IF/AND/OR/THEN/
 * ELSE/PRIORITY line text — `ControlRuleBuilder.tsx`'s save path. Default
 * priority 0 is omitted entirely rather than writing an explicit
 * `PRIORITY 0` line, reproducing the same "no PRIORITY clause present"
 * state a fresh parse of the result would find.
 */
export function serializeControlRule(draft: {
  priority: number;
  premises: RulePremise[];
  thenActions: RuleAction[];
  elseActions: RuleAction[];
}): string {
  const lines: string[] = [];
  draft.premises.forEach((p, i) => lines.push(premiseToLine(p, i === 0 ? "IF" : p.logicOp)));
  draft.thenActions.forEach((a, i) => lines.push(actionToLine(a, i === 0 ? "THEN" : "AND")));
  draft.elseActions.forEach((a, i) => lines.push(actionToLine(a, i === 0 ? "ELSE" : "AND")));
  if (draft.priority !== 0) lines.push(`PRIORITY ${draft.priority}`);
  return lines.join("\n");
}

/**
 * `ControlRuleBuilder.tsx`'s eligibility gate: can this rule be shown in the
 * structured visual editor at all, safely? Returns the rule's alias-free
 * parse (every premise/action already a concrete object/attribute triple)
 * when yes, `null` when no — the caller falls back to the plain-text editor.
 *
 * Two real risks this guards against: an `EXPRESSION`-referencing premise
 * has no corresponding UI at all; and a `VARIABLE`-referencing premise/
 * action resolves transparently into a concrete triple at parse time with
 * no remaining trace it was ever an alias, so editing an unrelated field
 * and saving would silently rewrite `IF MyAlias > 5` into
 * `IF NODE N1 DEPTH > 5` in the saved text.
 *
 * The fix: parse twice, once with the real `namedVariables` map and once
 * with an empty one. If both parses agree on how many premises/actions
 * came through, no line in this rule depended on alias resolution — safe to
 * show the alias-free version. Otherwise (or if an `EXPRESSION` premise or
 * a GAGE `PAST_PRECIP` premise is present, which has no visual editor
 * either) this rule stays text-only.
 */
export function canEditVisually(
  rule: { name: string; body: string },
  namedVariables: ReadonlyMap<string, RuleVariable>,
  expressionNames: ReadonlySet<string>
): ParsedControlRule | null {
  const withAliases = parseControlRule(rule, namedVariables, expressionNames);
  if (!withAliases) return null;
  if (
    withAliases.premises.some(
      (p) =>
        p.lhsExprName !== undefined ||
        (p.lhs.object === "GAGE" && p.lhs.attribute === "PAST_PRECIP") ||
        (p.rhsVar !== null && p.rhsVar.object === "GAGE" && p.rhsVar.attribute === "PAST_PRECIP")
    )
  ) {
    return null;
  }

  const withoutAliases = parseControlRule(rule, EMPTY_NAMED_VARIABLES, expressionNames);
  if (!withoutAliases) return null;
  if (
    withoutAliases.premises.length !== withAliases.premises.length ||
    withoutAliases.thenActions.length !== withAliases.thenActions.length ||
    withoutAliases.elseActions.length !== withAliases.elseActions.length
  ) {
    return null;
  }
  return withoutAliases;
}

/** GAGE -> raingage; NODE -> node; every link-carrying object keyword -> link. SIMULATION has no element of its own — absent from this map, so it never matches below. */
const OBJECT_KEYWORD_TO_CATEGORY: Partial<Record<RuleObjectKeyword, "node" | "link" | "raingage">> =
  {
    GAGE: "raingage",
    NODE: "node",
    LINK: "link",
    CONDUIT: "link",
    PUMP: "link",
    ORIFICE: "link",
    WEIR: "link",
    OUTLET: "link",
  };

/**
 * Does `rule` reference `name` (a node/link/raingage, matched
 * case-insensitively per the real engine's own object-name lookup) anywhere
 * in its premises or actions? Used by `useDeleteFlow.ts` to block deleting
 * an element a `[CONTROLS]` rule still names — real `controls.c` rejects
 * that `.inp` at load with `ERR_NAME`, this codebase has no fatal-load-error
 * path, so it blocks the delete instead, same as every other
 * still-referenced-elsewhere case in that file.
 */
export function controlRuleReferencesElement(
  rule: ParsedControlRule,
  category: "node" | "link" | "raingage",
  name: string
): boolean {
  const upperName = name.toUpperCase();
  function matches(v: RuleVariable | null | undefined): boolean {
    return (
      v !== null &&
      v !== undefined &&
      OBJECT_KEYWORD_TO_CATEGORY[v.object] === category &&
      v.id.toUpperCase() === upperName
    );
  }
  return (
    rule.premises.some((p) => matches(p.lhs) || matches(p.rhsVar)) ||
    rule.thenActions.some((a) => matches(a.target)) ||
    rule.elseActions.some((a) => matches(a.target))
  );
}
